import { useEffect, useState, useCallback } from "react";
import { canEditAgent } from "../../../utils/agentRbac";
import { TOOLSET_CATALOG } from "../../builder/toolsetCatalog";
import { agentApi, skillsApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import BindingHealthPill, { resolveBindingState } from "./wiring/BindingHealthPill";
import BindIntegrationModal from "./wiring/BindIntegrationModal";
import BindWorkflowModal from "./wiring/BindWorkflowModal";
import BindChannelModal from "./wiring/BindChannelModal";
import ScopedBindingsList from "./wiring/ScopedBindingsList";
import CredentialDrawer from "./wiring/CredentialDrawer";
import ModelsCard from "./ModelsCard";

// Phase B per agent-detail-viewer-v1.md §10 row 10 +
// agent-experience-redesign-v1.md §6 (Phase C wiring binding management).
//
// Renders MCP / OpenAPI / Channels / Workflows in four sections; each
// integration row carries a Bound toggle that creates / soft-deletes
// the agent_integration_bindings row via the A3 endpoints. Channel
// rows mutate agents.channels directly via A3's bind_channel /
// unbind_channel routes. Workflow rows mutate workflows.entry_agent_id.
//
// 412-banner deep-links to Admin per the existing pattern (preserved).

const Card = ({ title, count, children, action }) => (
  <div style={{
    padding: 16, borderRadius: 12,
    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
    boxShadow: MATERIALS.spatialShadow(),
  }}>
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      marginBottom: 12, gap: 12,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: COLORS.textDim,
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}>{title}{count != null ? ` (${count})` : ""}</div>
      {action}
    </div>
    {children}
  </div>
);

const ALL_CHANNELS = [
  { type: "whatsapp", label: "WhatsApp" },
  { type: "chat",     label: "Webchat"  },
  { type: "voice",    label: "Voice"    },
  { type: "sms",      label: "SMS"      },
  { type: "email",    label: "Email"    },
  { type: "webhook",  label: "Webhook"  },
];

// User-facing internal toolsets the WiringTab exposes for opt-in toggling.
// The platform-managed toolsets (builder / analytics / tester / admin / demo)
// auto-inject for specific agent kinds (Super Agent / Platform Tester /
// Demo Agent) and are NOT shown here — toggling them off on those agents
// would brick the agent. This list mirrors the user-opt-in subset
// documented in CLAUDE.md "Internal Tool Server".
// D5: one canonical menu shared with the Builder's ExpertWizard so the same
// agent shows the same toggles on every surface (this list previously omitted
// `analytics`). code_interpreter's Executable-Skills flag gating is preserved.
const USER_TOGGLEABLE_TOOLSETS = TOOLSET_CATALOG;

const cardActionStyle = {
  padding: "4px 10px", fontSize: 11, fontWeight: 600,
  borderRadius: 6, cursor: "pointer",
  background: "transparent",
  border: `1px solid ${COLORS.border}`,
  color: COLORS.textMuted,
  transition: "all 0.15s",
};

// Exported for unit testing
export function deepLinkPath(deep) {
  if (!deep) return "/admin";
  const params = new URLSearchParams();
  if (deep.tab) params.set("tab", deep.tab);
  if (deep.card) params.set("card", deep.card);
  if (deep.field) params.set("field", deep.field);
  if (deep.provider) params.set("provider", deep.provider);
  return `/admin?${params.toString()}`;
}

// Backward-compat fail-safe: if `auth_required` is missing (older API
// response), default it to TRUE so a missing credential is not silently
// hidden. Mirrors the backend's `missing_credentials` filter.
// Exported for unit testing
export function computeMissingRows(mcps, oapis) {
  const needsAuth = (r) => r.auth_required !== false;
  return [
    ...(mcps || [])
      .filter((m) => needsAuth(m) && !m.has_credential)
      .map((m) => ({ kind: "MCP", name: m.name, deep_link: m.deep_link })),
    ...(oapis || [])
      .filter((o) => needsAuth(o) && !o.has_credential)
      .map((o) => ({ kind: "OpenAPI", name: o.name, deep_link: o.deep_link })),
  ];
}

// Toggling an internal toolset writes the FULL desired enabled-set. Enabling
// adds (dedup); disabling removes. Only user-facing enabled names are carried.
// Exported for unit testing
export function computeToolsetToggle(toolsets, name, enabledNow) {
  const current = (toolsets || []).filter((t) => t.enabled).map((t) => t.name);
  return enabledNow
    ? current.filter((n) => n !== name)
    : Array.from(new Set([...current, name]));
}

// GET /api/skills/sandbox/status returns {tenant_enabled, effective_enabled,
// scripts_count, limits, ...}. `effective_enabled` is the fail-closed verdict —
// the one that says a run may actually happen — so it is what gates the
// code_interpreter toggle.
//
// This previously read a key named `enabled`, which the endpoint has never
// returned at either nesting level: it was undefined on every call, so the
// code_interpreter toggle rendered permanently disabled even with the workspace
// opt-in ON, and an agent that picked the toolset up from the Builder (which
// does not flag-gate) could not be unbound from here at all.
// Exported for unit testing
export function resolveExecEnabled(res) {
  return !!(res?.data?.effective_enabled ?? res?.effective_enabled);
}

// Strict version coercion. Number(null) and Number("") are both 0, so a naive
// Number() + isFinite would read a null-echoed version as a REAL version 0 and
// then 412 on every following write. Only a finite number, or a non-blank
// numeric string, counts.
function coerceVersion(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// Pull the server's CURRENT tree_version out of a 412 stale-write rejection.
// FastAPI nests HTTPException(detail={...}) under `detail`; a flat body is
// tolerated too. Returns null for anything that is not a stale_tree_version
// 412 — stale_updated_at and configuration_required also ride 412 and are not
// recoverable by retrying at a new version — so callers rethrow those untouched.
// Exported for unit testing
export function staleTreeVersionFrom(err) {
  if (!err || err.status !== 412) return null;
  const body = err.body || {};
  const d = body.detail && typeof body.detail === "object" ? body.detail : body;
  if (d?.error !== "stale_tree_version") return null;
  return coerceVersion(d.current_version);
}

// PUT /api/agents/{id} echoes the updated row (agents.py `_to_schema`), which
// carries the freshly-bumped tree_version. Absorbing it keeps the next write
// from re-sending the version we just superseded.
// Exported for unit testing
export function treeVersionFromUpdate(res) {
  return coerceVersion(res?.data?.tree_version);
}

function BoundToggle({ bound, busy, onClick, disabled }) {
  // Compact pill button — green when bound, neutral when not, with
  // confirm-on-click for unbind to avoid accidental detachment.
  return (
    <button
      type="button"
      disabled={busy || disabled}
      onClick={onClick}
      title={bound ? "Click to unbind from this agent" : "Click to bind to this agent"}
      style={{
        padding: "4px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600,
        cursor: (busy || disabled) ? "default" : "pointer",
        background: bound ? `${COLORS.green}25` : "transparent",
        border: `1px solid ${bound ? COLORS.green : COLORS.border}`,
        color: bound ? COLORS.green : COLORS.textMuted,
        opacity: busy ? 0.55 : 1,
        transition: "all 0.15s",
        whiteSpace: "nowrap",
      }}
    >
      {busy ? "…" : bound ? "● Bound" : "○ Not bound"}
    </button>
  );
}

export default function WiringTab({ tree, saveAgent, currentUser, onChannelsChanged }) {
  const agentId = tree?.agent?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busyKey, setBusyKey] = useState(null);  // {kind, id} mid-mutation
  const [confirm, setConfirm] = useState(null);   // {kind, id, label, action}
  const [bindModal, setBindModal] = useState(null); // "mcp" | "openapi" | "workflow" | null
  const [credDrawer, setCredDrawer] = useState(null); // {kind, row} | null
  // v13.x Supervisor RAG Routing v1 (Item 9): runtime capability +
  // auto-injected toolsets. Read-only display, fetched once on mount;
  // no mutation surface.
  const [capability, setCapability] = useState(null);
  // B1: workspace Executable Skills opt-in — gates the code_interpreter toggle.
  const [execEnabled, setExecEnabled] = useState(false);

  // The agent's tree_version, held LOCALLY rather than read off `tree` at write
  // time. The `tree` prop is fetched by AgentViewer only on agent switch /
  // refetchTick, but the server bumps tree_version on every PUT /agents/{id}
  // AND on every MCP / OpenAPI / workflow bind+unbind (`_bump_tree_version`) —
  // several of which this very tab performs. Reading the stale prop meant the
  // first toolset toggle succeeded and every one after it 412'd until the user
  // navigated away and back.
  const [treeVersion, setTreeVersion] = useState(tree?.agent?.tree_version ?? 0);
  const propTreeVersion = tree?.agent?.tree_version;
  useEffect(() => {
    // Re-seed whenever the parent genuinely refetches.
    setTreeVersion(propTreeVersion ?? 0);
  }, [propTreeVersion]);

  useEffect(() => {
    let alive = true;
    skillsApi.sandboxStatus()
      .then(res => { if (alive) setExecEnabled(resolveExecEnabled(res)); })
      .catch(() => { if (alive) setExecEnabled(false); });
    return () => { alive = false; };
  }, []);
  // RBAC: only admins/owners get plaintext reveal per §6.4. Developers
  // can write but cannot reveal stored values.
  const isAdmin = ["administrator", "owner"].includes(currentUser?.role);
  // RBAC: developer+ can bind/unbind per §5.5 grant table; business_user
  // and (legacy) supervisor roles see the toggles in disabled state.
  const canEdit = canEditAgent(currentUser?.role);

  const refresh = useCallback(() => {
    if (!agentId) return Promise.resolve();
    setLoading(true);
    setErr(null);
    return agentApi.getWiring(agentId)
      .then(res => setData(res?.data || null))
      .catch(e => setErr(e?.message || String(e)))
      .finally(() => setLoading(false));
  }, [agentId]);

  useEffect(() => {
    let cancelled = false;
    if (!agentId) return;
    setLoading(true);
    setErr(null);
    agentApi.getWiring(agentId)
      .then(res => { if (!cancelled) setData(res?.data || null); })
      .catch(e => { if (!cancelled) setErr(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agentId]);

  // Item 9: fetch runtime capability for the auto-injected section.
  // Failures here are non-blocking — the rest of WiringTab still renders.
  useEffect(() => {
    let cancelled = false;
    if (!agentId) return;
    agentApi.getCapability(agentId)
      .then(res => { if (!cancelled) setCapability(res?.data || null); })
      .catch(() => { /* swallow — non-critical UI surface */ });
    return () => { cancelled = true; };
  }, [agentId]);

  const runMutation = useCallback(async (kind, id, fn) => {
    setBusyKey(`${kind}:${id}`);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusyKey(null);
    }
  }, [refresh]);

  // Write the FULL desired toolset list under optimistic concurrency, then
  // absorb the server's freshly-bumped tree_version so the next write is not
  // born stale.
  const saveToolsets = useCallback(async (list, version) => {
    const res = await agentApi.update(
      agentId,
      { flow_config: { api: { enabled: true, internal_toolsets: list } } },
      { expectedTreeVersion: version },
    );
    const v = treeVersionFromUpdate(res);
    if (v != null) setTreeVersion(v);
    return res;
  }, [agentId]);

  // D3 (H3): send expected_tree_version so a concurrent edit trips the backend
  // 412 stale guard instead of silently clobbering.
  //
  // On a stale-version rejection, recover instead of dead-ending: re-fetch the
  // wiring envelope, recompute against that FRESH list, and retry once at the
  // version the server reported. `enabledNow` is captured at click time, so
  // passing it against the fresh list re-applies the user's original intent —
  // if a concurrent editor already moved the row to the desired state the
  // recompute is a no-op there, and their other toolset changes survive, which
  // re-sending the original array would not manage (the array is replaced
  // wholesale server-side). A second failure surfaces in the error banner.
  const toggleToolset = useCallback((name, enabledNow) => runMutation("toolset", name, async () => {
    const listFrom = (source) => computeToolsetToggle(source || [], name, enabledNow);
    try {
      await saveToolsets(listFrom(data?.toolsets), treeVersion);
    } catch (e) {
      const current = staleTreeVersionFrom(e);
      if (current == null) throw e;   // not a stale write — surface it as-is
      const fresh = await agentApi.getWiring(agentId);
      await saveToolsets(listFrom(fresh?.data?.toolsets), current);
    }
  }), [agentId, data, runMutation, saveToolsets, treeVersion]);

  const toggleMcp = useCallback((m) => {
    if (m.bound) {
      setConfirm({
        kind: "mcp", id: m.id, label: m.name,
        action: () => runMutation("mcp", m.id, () => agentApi.unbindMcp(agentId, m.id)),
      });
    } else {
      runMutation("mcp", m.id, () => agentApi.bindMcp(agentId, m.id));
    }
  }, [agentId, runMutation]);

  const toggleOpenapi = useCallback((o) => {
    if (o.bound) {
      setConfirm({
        kind: "openapi", id: o.id, label: o.name,
        action: () => runMutation("openapi", o.id, () => agentApi.unbindOpenapi(agentId, o.id)),
      });
    } else {
      runMutation("openapi", o.id, () => agentApi.bindOpenapi(agentId, o.id));
    }
  }, [agentId, runMutation]);

  // Channel bind/unbind mutates agents.channels — a field WiringTab's own
  // refresh() (GET /wiring) doesn't return at all, and which the parent
  // AgentViewer's `tree` only re-fetches on remount. Without
  // onChannelsChanged, this row's own Bound/Not-bound pill AND every
  // other tab reading tree.agent.channels (e.g. Overview) stay stale
  // until the user navigates away and back. onChannelsChanged triggers
  // AgentViewer's existing refetchTick mechanism (same one already used
  // for expert-row edits) to resync `tree` immediately.
  const toggleChannel = useCallback(async (channelType, currentlyBound) => {
    if (currentlyBound) {
      setConfirm({
        kind: "channel", id: channelType, label: channelType,
        action: async () => {
          await runMutation("channel", channelType, () => agentApi.unbindChannel(agentId, channelType));
          onChannelsChanged?.();
        },
      });
    } else {
      await runMutation("channel", channelType, () => agentApi.bindChannel(agentId, channelType));
      onChannelsChanged?.();
    }
  }, [agentId, runMutation, onChannelsChanged]);

  if (loading && !data) return <div style={{ color: COLORS.textDim }}>Loading wiring…</div>;
  if (err && !data)     return <div style={{ color: COLORS.pink }}>Failed to load wiring: {err}</div>;
  if (!data)            return <div style={{ color: COLORS.textDim }}>No wiring data.</div>;

  const toolsets = data.toolsets || [];
  const mcps     = data.mcp_servers || [];
  const oapis    = data.openapi_integrations || [];
  const wfs      = data.workflows || [];
  const missing  = data.missing_credentials || 0;

  // Channel sub-rows: every channel type, with `bound` derived from
  // agents.channels (which the backend writes synchronously in A3).
  const agentChannels = tree?.agent?.channels || [];
  const channelRows = ALL_CHANNELS.map(c => ({
    ...c,
    bound: agentChannels.includes(c.type),
  }));

  // Banner counts integrations that NEED a credential AND don't have
  // one. The backend's `missing_credentials` already filters on
  // `auth_required && !has_credential`; this mirrors that locally so
  // the deep-link list and the count agree row-for-row.
  //
  // Backward-compat: if `auth_required` is missing (older API
  // response), default it to TRUE so the banner doesn't silently
  // hide real problems during a partial rollout. (Same fail-safe
  // posture as the backend's helper.)
  const needsAuth = (r) => r.auth_required !== false;
  const missingRows = computeMissingRows(mcps, oapis);

  // Bindings matrix surfaces only on supervisor agents with at least one
  // expert. Regular agents (single-row case) fall through to the
  // per-section view below — the matrix would be silly with one row.
  const isSupervisor = (tree?.agent_kind || tree?.agent?.metadata_json?.agent_kind) === "supervisor";
  const familyExperts = (tree?.experts || []).map(e => ({ id: e.id, name: e.name }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Mutation-error banner. The load-failure path returns early above, so
          without this a FAILED bind / unbind / toolset toggle set `err` that
          nothing ever rendered — the pill flickered and silently did nothing,
          with the only trace in the browser console. */}
      {err && (
        <div role="alert" style={{
          padding: "10px 14px", borderRadius: 10,
          border: `1px solid ${COLORS.pink}66`,
          background: `${COLORS.pink}14`,
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <div style={{ flex: 1, minWidth: 200, fontSize: 12, color: COLORS.text }}>
            That change didn’t save: {err}
          </div>
          <button type="button" onClick={() => refresh()} style={{
            background: "transparent", border: `1px solid ${COLORS.border}`,
            color: COLORS.textDim, padding: "4px 10px", borderRadius: 6,
            fontSize: 11, cursor: "pointer",
          }}>Reload</button>
        </div>
      )}

      {/* Agent Model Transparency v1 — the tab leads with "Models this agent
          uses" (the tab is now labelled "Models"). Read-only; its own fetch of
          GET /model-resolution, independent of the wiring envelope below. */}
      <ModelsCard agentId={agentId} />

      {/* 412 deep-link banner (unchanged behavior — Admin destination) */}
      {missing > 0 && (
        <div style={{
          padding: "12px 16px", borderRadius: 10,
          border: `1px solid ${COLORS.pink}66`,
          background: `linear-gradient(90deg, ${COLORS.pink}1A 0%, transparent 80%)`,
          display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.pink, letterSpacing: "0.04em" }}>
              {`HTTP 412 · ${missing} integration${missing > 1 ? "s" : ""} missing credentials`}
            </div>
            <div style={{ fontSize: 11, color: COLORS.text, lineHeight: 1.5 }}>
              These rows will refuse to execute until the corresponding fields are configured.
              Each link below jumps directly to the missing field.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {missingRows.map((r, i) => (
                <a key={i} href={deepLinkPath(r.deep_link)}
                   style={{
                     padding: "4px 10px", borderRadius: 6, fontSize: 11,
                     background: COLORS.surface, color: COLORS.text,
                     border: `1px solid ${COLORS.pink}66`, textDecoration: "none",
                   }}>
                  {r.kind}: <strong>{r.name}</strong>
                  {r.deep_link?.field ? ` · ${r.deep_link.field}` : ""} →
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Scoped bindings list — supersedes BindingsMatrix.
          ONE row per integration in the tenant catalog with an
          explicit scope picker (which family members it's bound to).
          Atomic save via PUT /tree with optimistic concurrency.
          Hidden for regular agents (single-row family). */}
      {isSupervisor && familyExperts.length > 0 && (
        <ScopedBindingsList
          supervisor={{
            id: tree?.agent?.id,
            name: tree?.agent?.name,
            tree_version: tree?.agent?.tree_version ?? 0,
          }}
          experts={familyExperts}
          canEdit={canEdit}
          onSaved={() => { refresh(); }}
        />
      )}

      {/* Internal toolsets — toggleable. The 4 user-facing toolsets are
          listed; platform-managed toolsets (builder / analytics / tester /
          admin / demo) auto-inject for special agent kinds and are NOT
          exposed here to avoid users disabling them on Super Agent /
          Platform Tester / etc. by mistake.
          Toggle writes flow_config.api.internal_toolsets via PUT /agents/{id}
          (deep-merged server-side; arrays are replaced wholesale per
          _deep_merge semantics). */}
      {/* v13.x Supervisor RAG Routing v1 (Item 9): runtime auto-injected
          toolsets. Read-only — shown so the tenant author sees what
          tools the LLM will actually have at chat time, even when the
          opt-in toggle below is OFF. The dispatcher's Sub-option 2a
          auto-add inserts `query` when the agent has uploaded
          structured data (xlsx/csv/etc) AND the channel is not voice. */}
      {capability?.auto_injected_tools && capability.auto_injected_tools.length > 0 && (
        <Card title="Auto-injected at runtime" count={capability.auto_injected_tools.length}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {capability.auto_injected_tools.map(t => (
              <div key={t.toolset} style={{
                padding: 12, borderRadius: 8,
                background: `${COLORS.cyan || "#06b6d4"}15`,
                border: `1px solid ${COLORS.cyan || "#06b6d4"}40`,
              }}>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  marginBottom: 6, gap: 8,
                }}>
                  <span style={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 13, fontWeight: 600,
                    color: COLORS.cyan || "#06b6d4",
                  }}>{t.toolset}</span>
                  <span style={{
                    padding: "2px 6px", fontSize: 10, fontWeight: 700,
                    borderRadius: 4, letterSpacing: "0.06em",
                    background: `${COLORS.textDim}20`, color: COLORS.textDim,
                    textTransform: "uppercase",
                  }}>read-only</span>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
                  {t.description || `Auto-added because: ${t.reason}`}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card title="Internal toolsets" count={toolsets.filter(t => t.enabled).length}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {USER_TOGGLEABLE_TOOLSETS.map(ts => {
            const enabledNow = toolsets.some(t => t.name === ts.name && t.enabled);
            const busy = busyKey === `toolset:${ts.name}`;
            // B1: a flag-gated toolset (code_interpreter) can't be toggled on
            // until the workspace opt-in is enabled.
            const flagOff = !!ts.requiresFlag && !execEnabled;
            const onToggle = () => toggleToolset(ts.name, enabledNow);
            return (
              <div key={ts.name} style={{
                padding: "10px 12px", borderRadius: 8,
                border: `1px solid ${enabledNow ? COLORS.green + "44" : COLORS.border}`,
                background: COLORS.surface,
                display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, fontFamily: "monospace" }}>
                      {ts.name}
                    </span>
                    <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
                      {ts.tools.length} tool{ts.tools.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                    {ts.description}
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4, fontFamily: "monospace" }}>
                    {ts.tools.join(" · ")}
                  </div>
                  {flagOff && (
                    <div style={{ fontSize: 10, color: COLORS.orange, marginTop: 4 }}>
                      Enable Executable Skills in Admin → Executable Skills first.
                    </div>
                  )}
                </div>
                <BoundToggle
                  bound={enabledNow}
                  busy={busy}
                  disabled={!canEdit || flagOff}
                  onClick={onToggle}
                />
              </div>
            );
          })}
          {/* Read-only badge row for platform-managed toolsets currently
              enabled on this agent (e.g., builder on the Super Agent).
              Reminds the user the agent has those without offering a
              toggle that would brick the agent. */}
          {(() => {
            const userToggleableNames = new Set(USER_TOGGLEABLE_TOOLSETS.map(t => t.name));
            const platformEnabled = toolsets.filter(t => t.enabled && !userToggleableNames.has(t.name));
            if (platformEnabled.length === 0) return null;
            return (
              <div style={{
                marginTop: 4, padding: "8px 10px", borderRadius: 6,
                background: COLORS.bg,
                border: `1px dashed ${COLORS.border}`,
              }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: COLORS.textDim,
                  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
                }}>Platform-managed (read-only)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {platformEnabled.map(t => (
                    <span key={t.name} style={{
                      padding: "3px 8px", borderRadius: 6, fontSize: 11,
                      background: `${COLORS.accent}15`,
                      color: COLORS.text,
                      border: `1px solid ${COLORS.accent}33`,
                      fontFamily: "monospace",
                    }}>{t.name}</span>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </Card>

      {/* MCP servers — bind/unbind toggle + health pill */}
      <Card title="MCP servers" count={mcps.length}
        action={canEdit && (
          <button type="button" onClick={() => setBindModal("mcp")} style={cardActionStyle}>
            + Bind
          </button>
        )}>

        {mcps.length === 0 ? (
          <div style={{ fontSize: 12, color: COLORS.textDim }}>No MCP servers registered.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {mcps.map(m => (
              <div key={m.id} style={{
                padding: "10px 12px", borderRadius: 8,
                border: `1px solid ${m.bound ? COLORS.green + "44" : COLORS.border}`,
                background: COLORS.surface,
                display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{m.name}</span>
                    <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>{m.server_id}</span>
                    <BindingHealthPill state={resolveBindingState(m)} size="sm" />
                    {!m.enabled && <span style={{ fontSize: 10, color: COLORS.textDim }}>(disabled)</span>}
                  </div>
                  {m.description && (
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>{m.description}</div>
                  )}
                  {m.last_error && (
                    <div style={{ fontSize: 11, color: COLORS.pink, marginTop: 4, fontFamily: "monospace" }}>
                      last_error: {m.last_error}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                  <BoundToggle
                    bound={m.bound}
                    busy={busyKey === `mcp:${m.id}`}
                    disabled={!canEdit}
                    onClick={() => toggleMcp(m)}
                  />
                  {canEdit && (() => {
                    // Three-state pill (replaces the old two-state).
                    // no auth needed → neutral grey "No credential needed"
                    // missing creds  → red "Configure →" (action required)
                    // has creds      → cyan "Edit credentials"
                    const needs = needsAuth(m);
                    const ok = m.has_credential;
                    const color = !needs ? COLORS.textDim
                                : !ok    ? (COLORS.pink || "#ef4444")
                                :          COLORS.cyan;
                    const label = !needs ? "No credential needed"
                                : !ok    ? "Configure →"
                                :          "Edit credentials";
                    return (
                      <button type="button" onClick={() => setCredDrawer({ kind: "mcp", row: m })}
                        disabled={!needs}
                        style={{
                          fontSize: 10, cursor: needs ? "pointer" : "default",
                          color, background: "transparent",
                          border: `1px solid ${color}55`,
                          padding: "3px 8px", borderRadius: 5,
                          opacity: needs ? 1 : 0.7,
                        }}>
                        {label}
                      </button>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* OpenAPI integrations — bind/unbind toggle + health pill */}
      <Card title="OpenAPI integrations" count={oapis.length}
        action={canEdit && (
          <button type="button" onClick={() => setBindModal("openapi")} style={cardActionStyle}>
            + Bind
          </button>
        )}>

        {oapis.length === 0 ? (
          <div style={{ fontSize: 12, color: COLORS.textDim }}>No OpenAPI integrations.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {oapis.map(o => (
              <div key={o.id} style={{
                padding: "10px 12px", borderRadius: 8,
                border: `1px solid ${o.bound ? COLORS.green + "44" : COLORS.border}`,
                background: COLORS.surface,
                display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{o.name}</span>
                    <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>{o.slug}</span>
                    <BindingHealthPill state={resolveBindingState(o)} size="sm" />
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>{o.auth_method}</span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                    {o.base_url} · {o.enabled_operations_count} operations enabled
                    {!o.enabled && " · (disabled)"}
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2, fontFamily: "monospace" }}>
                    credential_ref: {o.credential_ref}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                  <BoundToggle
                    bound={o.bound}
                    busy={busyKey === `openapi:${o.id}`}
                    disabled={!canEdit}
                    onClick={() => toggleOpenapi(o)}
                  />
                  {canEdit && (() => {
                    const needs = needsAuth(o);
                    const ok = o.has_credential;
                    const color = !needs ? COLORS.textDim
                                : !ok    ? (COLORS.pink || "#ef4444")
                                :          COLORS.cyan;
                    const label = !needs ? "No credential needed"
                                : !ok    ? "Configure →"
                                :          "Edit credentials";
                    return (
                      <button type="button" onClick={() => setCredDrawer({ kind: "openapi", row: o })}
                        disabled={!needs}
                        style={{
                          fontSize: 10, cursor: needs ? "pointer" : "default",
                          color, background: "transparent",
                          border: `1px solid ${color}55`,
                          padding: "3px 8px", borderRadius: 5,
                          opacity: needs ? 1 : 0.7,
                        }}>
                        {label}
                      </button>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Channels — toggle adds/removes from agents.channels.
          Per §6.3 + §16.2 #8: dual-mode <BindChannelModal> handles
          both "connect existing" + "create new" paths (F9). The
          per-channel chip-strip below is the quick-toggle for
          already-configured channels. */}
      <Card title="Channels" count={agentChannels.length}
        action={canEdit && (
          <button type="button" onClick={() => setBindModal("channel")} style={cardActionStyle}>
            + Bind
          </button>
        )}>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {channelRows.map(c => (
            <div key={c.type} style={{
              padding: "8px 12px", borderRadius: 8,
              border: `1px solid ${c.bound ? COLORS.green + "44" : COLORS.border}`,
              background: COLORS.surface,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{ fontSize: 12, color: COLORS.text, fontWeight: 600, minWidth: 70 }}>{c.label}</span>
              <BoundToggle
                bound={c.bound}
                busy={busyKey === `channel:${c.type}`}
                disabled={!canEdit}
                onClick={() => toggleChannel(c.type, c.bound)}
              />
            </div>
          ))}
        </div>
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 10 }}>
          Tenant-level channel credentials are configured via Admin → Integrations.
          Toggling here only attaches / detaches this agent from the channel's routing.
        </div>
      </Card>

      {/* Workflows — bind toggle (unbind via row); + Bind opens
          dual-mode picker (existing tenant workflow vs new cron job). */}
      <Card title="Workflows" count={wfs.length}
        action={canEdit && (
          <button type="button" onClick={() => setBindModal("workflow")} style={cardActionStyle}>
            + Bind
          </button>
        )}>

        {wfs.length === 0 ? (
          <div style={{ fontSize: 12, color: COLORS.textDim }}>
            No workflows attached to this agent. Create one in Schedules and bind it here.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {wfs.map(w => (
              <div key={w.id} style={{
                padding: "10px 12px", borderRadius: 8,
                border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{w.name}</span>
                    {w.version_number != null && (
                      <span style={{ fontSize: 10, color: COLORS.textDim }}>v{w.version_number}</span>
                    )}
                    {w.status && (
                      <span style={{
                        fontSize: 10, padding: "2px 7px", borderRadius: 5,
                        background: `${COLORS.accent}22`, color: COLORS.accentText,
                        border: `1px solid ${COLORS.accent}55`,
                      }}>{w.status}</span>
                    )}
                  </div>
                  {w.description && (
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>{w.description}</div>
                  )}
                </div>
                <BoundToggle
                  bound={true}
                  busy={busyKey === `workflow:${w.id}`}
                  disabled={!canEdit}
                  onClick={() => setConfirm({
                    kind: "workflow", id: w.id, label: w.name,
                    action: () => runMutation("workflow", w.id, () => agentApi.unbindWorkflow(agentId, w.id)),
                  })}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Bind modals — opened from "+ Bind" buttons on the section
          headers. On success they call onBound which refresh()es the
          wiring envelope, then close. */}
      {bindModal === "mcp" && (
        <BindIntegrationModal
          kind="mcp"
          agentId={agentId}
          currentUser={currentUser}
          onClose={() => setBindModal(null)}
          onBound={() => refresh()}
        />
      )}
      {bindModal === "openapi" && (
        <BindIntegrationModal
          kind="openapi"
          agentId={agentId}
          currentUser={currentUser}
          onClose={() => setBindModal(null)}
          onBound={() => refresh()}
        />
      )}
      {bindModal === "workflow" && (
        <BindWorkflowModal
          agentId={agentId}
          agentName={tree?.agent?.name}
          onClose={() => setBindModal(null)}
          onBound={() => refresh()}
        />
      )}
      {bindModal === "channel" && (
        <BindChannelModal
          agentId={agentId}
          agentChannels={agentChannels}
          onClose={() => setBindModal(null)}
          onBound={() => { refresh(); onChannelsChanged?.(); }}
        />
      )}

      {/* C2 — Inline credential CRUD drawer (replaces deep-link to
          Admin → Credentials when origin is Detail Viewer per design
          §6.5). On save, refresh()es the wiring envelope so the
          health pill updates. */}
      {credDrawer && (
        <CredentialDrawer
          kind={credDrawer.kind}
          row={credDrawer.row}
          canRevealPlaintext={isAdmin}
          onClose={() => setCredDrawer(null)}
          onSaved={() => refresh()}
        />
      )}

      {/* Confirm-unbind modal (single primitive used across kinds) */}
      {confirm && (
        <div onClick={() => setConfirm(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 200,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 12, padding: 20, maxWidth: 420, width: "90%",
            boxShadow: MATERIALS.spatialShadow ? MATERIALS.spatialShadow() : "0 16px 40px rgba(0,0,0,0.5)",
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>
              Unbind {confirm.kind} from this agent?
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16, lineHeight: 1.5 }}>
              <strong>{confirm.label}</strong> will be detached from this agent. The integration row stays
              in your tenant — you can re-attach it any time. Existing in-flight conversations may continue
              briefly until their cache expires.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirm(null)} style={{
                background: "transparent", border: `1px solid ${COLORS.border}`,
                color: COLORS.textDim, padding: "6px 14px", borderRadius: 6,
                fontSize: 12, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={async () => { const a = confirm.action; setConfirm(null); await a(); }} style={{
                background: COLORS.pink || "#ef4444", color: "#fff", border: "none",
                padding: "6px 14px", borderRadius: 6,
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>Unbind</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
